import React from 'react';
import { Dropdown, Menu } from 'antd';
import { ArrowDownIcon, Button, Flex } from 'glints-aries';
import { space8 } from 'glints-aries/es/@next/utilities/spacing';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { COUNTRY_DOMAINS } from '../../common/constants';
import { countryCodeIsVietnam } from '../../common/helpers/countryCodeHelper';
import useCandidateUrl from '../../common/hooks/useCandidateUrl';
import useGetRefCodeParam from '../../common/hooks/useGetRefCodeParam';
import useIsChatEnabled from '../../common/hooks/useIsChatEnabled';
import { Company } from '../../common/interfaces/entities';
import config, { getConfig } from '../../config';
import {
  getAreFeaturesFetched,
  getIsFeatureEnabled,
} from '../../selectors/features';
import { getIsLoggedIn } from '../../selectors/session';
import AppLanguageSwitcher from '../AppLanguage/AppLanguageSwitcher';
import { MenuItem, MenuType } from '../Platform/Constants';
import { UnleashFeatureNames } from '../Unleash/featureNames';
import { NAVBAR_LOGO_BASED_ON_COUNTRY } from './Constants';
import CTAButtonLoggedIn from './CTAButtonLoggedIn';
import CTAChat from './CTAChat';
import DesktopHeaderNavigation from './DesktopHeaderNavigation';
import DropdownItem from './DropdownItem';
import {
  LeftMenu,
  NavbarContainer,
  NavbarLogo,
  RightMenu,
} from './NavbarCommon.sc';
import {
  DesktopLeftSection,
  DesktopNavbarLogo,
  DesktopRightSection,
  DropdownLink,
  DropdownLinkLabel,
  DropdownMenu,
  HeaderActions,
  HeaderDivider,
  HeaderLanguageMenu,
  HeaderUserMenu,
  StyledBadge,
} from './NavbarDesktop.sc';
import { NavbarItem } from './NavbarItem';
import {
  disableLink,
  filteredMenuItems,
  renderItemLabel,
} from './navigationUtils';
import { useGetLandingNavItems } from './useGetLandingNavItems';
import UserMenu from './UserMenu/UserMenu';

const NavbarDesktop: React.FC<NavbarDesktopProps> = ({
  authenticated,
  company,
  fromJobEditPage,
  fromOnboardingPage,
  hideSignUp,
  menuItems,
  permissions,
  role,
}) => {
  const intl = useIntl();

  const notifyNewVNMonetizedFeatures = useSelector(state =>
    getIsFeatureEnabled(
      state,
      UnleashFeatureNames.empNotifyNewVNMonetizedFeatures
    )
  );
  const isVNCompany = countryCodeIsVietnam((company as Company)?.CountryCode);

  const areFeaturesFetched = useSelector(getAreFeaturesFetched);
  const empCanAccessMessaging = useIsChatEnabled();
  const landingNavItems = useGetLandingNavItems();
  const candidateUrl = useCandidateUrl();

  const isLoggedIn = useSelector(getIsLoggedIn);

  const referralCode = useGetRefCodeParam();

  if (authenticated) {
    const navigationItems = filteredMenuItems(menuItems, true);
    const logo =
      NAVBAR_LOGO_BASED_ON_COUNTRY[getConfig().COUNTRY] ??
      NAVBAR_LOGO_BASED_ON_COUNTRY[COUNTRY_DOMAINS.default];

    return (
      <NavbarContainer>
        <DesktopLeftSection>
          <DesktopNavbarLogo to="/dashboard">
            <img src={logo} alt="Glints for Employers" />
          </DesktopNavbarLogo>
          <If condition={areFeaturesFetched}>
            <DesktopHeaderNavigation
              company={company}
              intl={intl}
              items={navigationItems}
              permissions={permissions}
              role={role}
            />
          </If>
        </DesktopLeftSection>
        <DesktopRightSection>
          <HeaderActions>
            <If condition={empCanAccessMessaging}>
              <CTAChat compact={true} />
            </If>
            <CTAButtonLoggedIn
              compact={true}
              fromJobEditPage={fromJobEditPage}
              fromOnboardingPage={fromOnboardingPage}
            />
          </HeaderActions>
          <HeaderDivider />
          <HeaderUserMenu>
            <UserMenu
              menuItems={menuItems.filter(link => link.type === MenuType.user)}
            />
          </HeaderUserMenu>
          <HeaderDivider />
          <HeaderLanguageMenu>
            <AppLanguageSwitcher
              hideDivider={true}
              hideIcon={true}
              variant="button"
            />
          </HeaderLanguageMenu>
        </DesktopRightSection>
      </NavbarContainer>
    );
  }

  return (
    <NavbarContainer>
      <LeftMenu mode="horizontal" selectedKeys={[]}>
        <NavbarLogo to={isLoggedIn ? '/dashboard' : '/'} />
        <If condition={areFeaturesFetched}>
          {filteredMenuItems(menuItems, Boolean(authenticated)).map(item => {
            if (item.children) {
              return (
                <Menu.Item key={item.value}>
                  <Dropdown
                    overlay={
                      <DropdownMenu>
                        <DropdownItem items={item.children} />
                      </DropdownMenu>
                    }
                  >
                    <DropdownLink>
                      <DropdownLinkLabel>
                        {renderItemLabel(intl, item.label)}
                      </DropdownLinkLabel>
                      <ArrowDownIcon />
                    </DropdownLink>
                  </Dropdown>
                </Menu.Item>
              );
            } else {
              const navItem = (
                <NavbarItem
                  activeClassName="ant-menu-item-selected"
                  key={item.value}
                  to={item.to || ''}
                  disabled={
                    item.canBeDisabled &&
                    disableLink(role, company, permissions)
                  }
                  badge={item.badge}
                  onClick={item.onClick}
                >
                  <Flex alignItems="center" style={{ gap: space8 }}>
                    {renderItemLabel(intl, item.label)}
                    <If
                      condition={
                        isVNCompany &&
                        notifyNewVNMonetizedFeatures &&
                        ['features'].includes(item.value)
                      }
                    >
                      <StyledBadge>
                        {intl.formatMessage({
                          id: 'text-new',
                          defaultMessage: 'New',
                        })}
                      </StyledBadge>
                    </If>
                  </Flex>
                </NavbarItem>
              );

              return navItem;
            }
          })}
        </If>

        {!isLoggedIn &&
          landingNavItems.items.map(item => {
            if (item.subNav?.length) {
              return (
                <Menu.Item key={item.label}>
                  <Dropdown
                    overlay={
                      <DropdownMenu>
                        <DropdownItem
                          key={item.label}
                          items={item.subNav.map(i => ({
                            value: i.label,
                            label: i.label,
                            to: referralCode
                              ? `${i.url}?refCode=${referralCode}`
                              : i.url,
                          }))}
                        />
                      </DropdownMenu>
                    }
                  >
                    <DropdownLink>
                      <DropdownLinkLabel>{item.label}</DropdownLinkLabel>
                      <ArrowDownIcon />
                    </DropdownLink>
                  </Dropdown>
                </Menu.Item>
              );
            } else {
              return (
                <NavbarItem key={item.label} to={item.url}>
                  {item.label}
                </NavbarItem>
              );
            }
          })}
      </LeftMenu>

      <RightMenu mode="horizontal" selectedKeys={[]} disabledOverflow={true}>
        <NavbarItem to="/login">
          <FormattedMessage
            id="modules.Navigation.NavbarDesktop.login"
            defaultMessage="Login"
          />
        </NavbarItem>

        <If condition={!hideSignUp && !['id', 'sg'].includes(config.COUNTRY)}>
          <NavbarItem to="/signup">
            <FormattedMessage
              id="modules.Navigation.NavbarDesktop.sign.up"
              defaultMessage="Sign Up"
            />
          </NavbarItem>
        </If>

        <NavbarItem to={candidateUrl}>
          <FormattedMessage
            id="modules.Navigation.CTAButton.candidates"
            defaultMessage="Job Seekers"
          />
        </NavbarItem>
        <NavbarItem to={landingNavItems?.button?.url || ''}>
          <Button variant="solid-blue">{landingNavItems?.button?.label}</Button>
        </NavbarItem>

        <div
          style={{
            padding: '8px 0',
            height: '100%',
          }}
        >
          <AppLanguageSwitcher />
        </div>
      </RightMenu>
    </NavbarContainer>
  );
};

type NavbarDesktopProps = {
  authenticated?: boolean;
  fromJobEditPage: boolean;
  fromOnboardingPage: boolean;
  hideSignUp: boolean;
  menuItems: MenuItem[];
  company: unknown;
  permissions: unknown;
  role: string;
  hasNewUnreadMessages: boolean;
};

export default NavbarDesktop;
